import { useNavigate } from 'react-router-dom';
import type { ArticlePost } from '@/lib/articles';

interface Props {
  posts: ArticlePost[];
}

const FeaturedArticles = ({ posts }: Props) => {
  const navigate = useNavigate();

  if (!posts.length) return null;

  const [main, ...rest] = posts;

  return (
    <div className="max-w-7xl mx-auto px-6 pb-2">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-1 h-5 bg-[#0A1628] rounded-full" />
        <h2 className="text-sm font-bold text-[#0A1628] tracking-wide uppercase">精選導師文章</h2>
        <span className="ml-1 px-2 py-0.5 bg-rose-50 text-rose-500 text-[11px] font-bold rounded-full">
          編輯推薦
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main featured */}
        <div
          onClick={() => navigate(`/blog/${main.id}`)}
          className="lg:col-span-2 group cursor-pointer rounded-2xl overflow-hidden relative h-72 lg:h-80"
        >
          <img
            src={main.coverImage}
            alt={main.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute inset-0 p-6 flex flex-col justify-end">
            <span className={`self-start bg-gradient-to-r ${main.gradient} text-white text-[11px] font-bold px-3 py-1 rounded-full mb-3`}>
              {main.category}
            </span>
            <h3 className="text-xl font-bold text-white leading-snug mb-2 group-hover:text-white/90 transition-colors">
              {main.title}
            </h3>
            <p className="text-white/70 text-xs line-clamp-2 mb-4 leading-relaxed">{main.excerpt}</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full overflow-hidden ring-2 ring-white/30">
                  <img src={main.authorAvatar} alt={main.author} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <p className="text-white text-xs font-semibold">{main.author}</p>
                  <p className="text-white/60 text-[10px]">{main.authorRole} · {main.authorCompany}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-white/60">
                <span className="flex items-center gap-1"><i className="ri-time-line" />{main.readTime} 分鐘</span>
                <span className="flex items-center gap-1"><i className="ri-eye-line" />{(main.views / 1000).toFixed(1)}K</span>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary featured */}
        <div className="flex flex-col gap-4">
          {rest.map((post) => (
            <div
              key={post.id}
              onClick={() => navigate(`/blog/${post.id}`)}
              className="group cursor-pointer rounded-xl overflow-hidden relative h-[9.5rem] flex-1"
            >
              <img
                src={post.coverImage}
                alt={post.title}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
              <div className="absolute inset-0 p-4 flex flex-col justify-end">
                <span className={`self-start bg-gradient-to-r ${post.gradient} text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full mb-1.5`}>
                  {post.category}
                </span>
                <h4 className="text-sm font-bold text-white leading-snug mb-2 line-clamp-2 group-hover:text-white/90">
                  {post.title}
                </h4>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full overflow-hidden ring-1 ring-white/30">
                    <img src={post.authorAvatar} alt={post.author} className="w-full h-full object-cover object-top" />
                  </div>
                  <span className="text-white/70 text-[10px]">{post.author} · {post.authorCompany}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FeaturedArticles;
